/**
 * @file IA des chats (Follow, Chase, Attack, Reposition, Assist) : formation derriere le chef, choix de cible auto-aim, rotation de visee.
 */
import type { EntityId } from '@shared/types';
import type { SimContext } from '../Context';
import { isShootable } from '../combat/damage';
import type { Cat } from '../ecs/components';
import type { With } from '../ecs/World';
import { angleTo, dist, rotateToward } from '../math/geometry';
import { clampPointToBounds, clampToBounds, resolveSolids } from './movement';
import type { System } from './System';

/** Vitesse de rotation de la visee (rad/s) ; equivalent de CAT_ANIM.shoot.aimTurnSpeedDegPerSec sans dependre du rendu. */
const AIM_TURN_SPEED = 4 * Math.PI;
/** Portee par defaut d'un chat sans arme (le chef du hub, de l'aventure...). */
const UNARMED_RANGE = 8;
/**
 * Portee de tir reelle : le bord de la cible (centre - rayon de collision) est a portee. C'est LA meme regle pour choisir une cible
 * (ici) et pour tirer (WeaponSystem) : avant, la grille renvoyait des cibles a "portee + rayon" que l'arme refusait de tirer
 * (chat en etat "attaque", sans tirer ni avancer).
 */
export const inReach = (x: number, y: number, target: With<'target' | 'health' | 'collider'>, range: number): boolean =>
  dist(x, y, target.transform.x, target.transform.y) - target.collider.radius <= range;

const FOLLOW_SNAP = 0.4;
const FOLLOW_BOOST = 1.15;
const CHASE_SEARCH_RATIO = 1.5;
const CHASE_STOP_RATIO = 0.92;
/** La poursuite reste a l'interieur de ce rapport du rayon libre (marge avant la reprise en laisse). */
const CHASE_RADIUS_RATIO = 0.9;
/** Part du decalage lateral de formation conservee pendant la poursuite d'une cible. */
const CHASE_LANE = 0.6;
const MOVED_EPS = 0.01;
/** Orientation d'un suiveur : ne suit que les vrais deplacements (pas les micro-corrections) et tourne a vitesse limitee. */
const TURN_MOVE_EPS = 0.03;
const BODY_TURN_SPEED = 3 * Math.PI;

type Tgt = With<'target' | 'health' | 'collider'>;

const targetEntity = (ctx: SimContext, id: EntityId | null): Tgt | null => {
  if (id === null) return null;
  const e = ctx.rt.world.get(id);
  return e && isShootable(ctx, e) ? e : null;
};

/** Meilleure cible dans `range` : priorite decroissante, puis distance, puis id (ordre de la grille). */
const pickTarget = (ctx: SimContext, x: number, y: number, range: number): Tgt | null => {
  const best: { e: Tgt | null; prio: number; d2: number } = { e: null, prio: -1, d2: Number.POSITIVE_INFINITY };
  ctx.rt.targetGrid.queryCircle(x, y, range, (id, d2) => {
    const e = ctx.rt.world.get(id);
    if (!e || !isShootable(ctx, e) || !inReach(x, y, e, range)) return;
    const prio = e.target.priority;
    if (prio > best.prio || (prio === best.prio && d2 < best.d2)) {
      best.e = e;
      best.prio = prio;
      best.d2 = d2;
    }
  });
  return best.e;
};

/** Place de formation du suiveur `i` (decalage dans le repere du chef : derriere lui, en V). */
const formationSpot = (ctx: SimContext, i: number, leader: With<'cat'>): { x: number; y: number } => {
  const off = ctx.services.team.formationOffset(i);
  const rot = ctx.services.team.formationHeading(leader) - Math.PI / 2;
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  // La place est ramenee dans les bornes (mur du chemin, barriere Monster) : un suiveur ne vise jamais un point inatteignable.
  return clampPointToBounds(ctx, leader.transform.x + off.dx * c - off.dy * s, leader.transform.y + off.dx * s + off.dy * c, spotScratch);
};
const spotScratch = { x: 0, y: 0 };

/** Annule le pas de poursuite s'il fait sortir (ou s'eloigner) `e` du cercle de rayon `r` autour du chef : jamais de recul brusque. */
const keepInside = (e: With<'cat'>, leader: With<'cat'>, r: number, prevX: number, prevY: number): void => {
  const d = dist(e.transform.x, e.transform.y, leader.transform.x, leader.transform.y);
  const before = dist(prevX, prevY, leader.transform.x, leader.transform.y);
  if (d > r && d > before) {
    e.transform.x = prevX;
    e.transform.y = prevY;
  }
};

const stepToward = (e: With<'cat'>, gx: number, gy: number, maxStep: number): void => {
  const d = dist(e.transform.x, e.transform.y, gx, gy);
  if (d < 1e-6) return;
  const step = Math.min(d, maxStep);
  e.transform.x += ((gx - e.transform.x) / d) * step;
  e.transform.y += ((gy - e.transform.y) / d) * step;
};

/**
 * Pour chaque chat : ciblage auto-aim (si le mode autorise le tir), rotation de visee ; les suiveurs rejoignent leur place de
 * formation derriere le chef (ou poursuivent une cible hors de portee quand le chef est immobile).
 */
export const CatSystem: System = {
  id: 'cats',
  update(ctx: SimContext, dt: number): void {
    const team = ctx.services.team;
    const cats = team.cats();
    const leader = cats[0];
    if (!leader) return;
    const canFire = ctx.rt.control.canFire;
    const baseSpeed = ctx.stats.getNumber('moveSpeed');

    cats.forEach((e, i) => {
      const cat: Cat = e.cat;
      const weapon = team.resolveFor(cat);
      const range = weapon?.range ?? UNARMED_RANGE;
      const { x, y } = e.transform;

      // ---- ciblage
      let target: Tgt | null = null;
      if (canFire) {
        if (cat.isHelper) {
          const lt = targetEntity(ctx, leader.cat.targetId);
          if (lt && inReach(x, y, lt, range)) target = lt;
        }
        if (!target) {
          const cur = targetEntity(ctx, cat.targetId);
          if (cur && inReach(x, y, cur, range)) target = cur;
        }
        target ??= pickTarget(ctx, x, y, range);
      }
      cat.targetId = target ? target.id : null;

      // ---- deplacement des suiveurs (le chef est deplace par ControlSystem)
      if (i > 0) {
        const combat = ctx.config.balance.combat;
        const toLeader = dist(x, y, leader.transform.x, leader.transform.y);
        // LIBRES dans un rayon autour du chef : ils ne suivent pas tant qu'ils y restent. Hors du rayon (ou tant qu'ils n'ont pas
        // rejoint le cercle interieur apres l'avoir quitte) ils rejoignent leur place de formation.
        const catchingUp = toLeader > combat.followRadius || (cat.aiState === 'follow' && toLeader > combat.followRadius * combat.followReturnRatio);
        let state: Cat['aiState'] = 'idle';
        if (catchingUp) {
          state = 'follow';
          const spot = formationSpot(ctx, i, leader);
          const d = dist(x, y, spot.x, spot.y);
          const speed = d > FOLLOW_SNAP ? baseSpeed * FOLLOW_BOOST * (d > 3 ? 1.6 : 1) : baseSpeed;
          stepToward(e, spot.x, spot.y, speed * dt);
        } else if (target) {
          state = cat.isHelper ? 'assist' : 'attack'; // cible a portee : le suiveur tient sa position
        } else if (canFire) {
          const far = ctx.rt.targetGrid.nearest(x, y, range * CHASE_SEARCH_RATIO, (id) => {
            const o = ctx.rt.world.get(id);
            return !!o && isShootable(ctx, o);
          });
          const o = far !== null ? ctx.rt.world.get(far) : undefined;
          if (o) {
            const d = dist(x, y, o.transform.x, o.transform.y);
            const stop = range * CHASE_STOP_RATIO;
            if (d > stop) {
              state = 'chase';
              // Couloir lateral propre a chaque suiveur : ils n'arrivent pas tous au meme point devant la cible.
              const lane = ctx.services.team.formationOffset(i).dx * CHASE_LANE;
              stepToward(e, o.transform.x + lane, o.transform.y - stop * 0.5, Math.min(baseSpeed * dt, d - stop));
              keepInside(e, leader, combat.followRadius * CHASE_RADIUS_RATIO, x, y);
            }
          }
        }
        if (resolveSolids(ctx, e, e.collider?.radius ?? 0.55) && state === 'idle') state = 'reposition';
        clampToBounds(ctx, e);
        const moved = Math.hypot(e.transform.x - x, e.transform.y - y);
        cat.moving = moved > MOVED_EPS;
        if (!target) {
          const heading = moved > TURN_MOVE_EPS ? Math.atan2(e.transform.y - y, e.transform.x - x) : leader.transform.rot;
          e.transform.rot = rotateToward(e.transform.rot, heading, BODY_TURN_SPEED * dt);
        }
        cat.aiState = state;
      } else {
        cat.aiState = target ? 'attack' : cat.moving ? 'follow' : 'idle';
      }

      // ---- visee
      const goal = target ? angleTo(e.transform.x, e.transform.y, target.transform.x, target.transform.y) : e.transform.rot;
      cat.aimAngle = rotateToward(cat.aimAngle, goal, AIM_TURN_SPEED * dt);
    });
  },
};
